import AltContainer from 'alt-container';
import connectToStores from 'alt-utils/lib/connectToStores';
import React from 'react';
import AdminEditUserActions from 'actions/admin/AdminEditUserActions';
import AdminAccountIntegrationStore from 'stores/admin/AdminAccountIntegrationStore';
import AdminAccountIntegrationActions from 'actions/admin/AdminAccountIntegrationActions';
import {Map} from 'immutable';
import {UserIntegrationExtern} from 'records/EditUser';
import displayNameForIntegration from 'utils/integrations/displayNameForIntegration';
import integrationsAreEqual from 'utils/integrations/integrationsAreEqual';
import AdminFormDataActions from 'actions/admin/AdminFormDataActions';
import AdminFormDataStore from 'stores/admin/AdminFormDataStore';
import AuthStore from 'stores/AuthStore';
import AdminEditUserStore from 'stores/admin/user/AdminEditUserStore';
import AdminEditUser from 'components/pure/admin/user/AdminEditUser';
import AccountActions from 'actions/AccountActions';
import AccountStore from 'stores/AccountStore';
import JobActions from '@store/job/JobActions';
import JobStore from '@store/job/JobStore';
import AdminUserActions from '@store/admin/AdminUserActions';
const IGNORED_INTEGRATIONS = ['Sonero'];

/**
 * AltContainer for <AdminEditUser/>
 */
@connectToStores
export default class AdminEditUserContainer extends React.Component {
  componentDidMount() {
    // Populates available seats
    AdminUserActions.fetchUserTable();
    AccountActions.hasSlack();
    // Fetch the current user account's formData to support rendering a user's
    // avatar with a next-color
    AdminFormDataActions.fetchFormData();
    // Fetch the current user account's integrations metadata
    AdminAccountIntegrationActions.fetchAdminAccountIntegrations();
    // Fetch jobs for jobs drop-down select
    JobActions.getJobsInAccount().then().catch(console.error);
    if (this.props.params.userId) {
      AdminEditUserActions.get(this.props.params.userId);
    } else {
      AdminEditUserActions.reset();
    }
  }

  componentWillUnmount() {
    AdminEditUserActions.reset();
  }

  static getStores() {
    return [
      AccountStore,
      AdminEditUserStore,
      AdminFormDataStore,
      AdminAccountIntegrationStore,
      JobStore,
    ];
  }

  static getPropsFromStores() {
    const {hasSlack, hasSlackLoaded} = AccountStore.getState();
    const editState = AdminEditUserStore.getState();
    const loggedInUser = AuthStore.getState().user;
    const accountFormData = AdminFormDataStore.getState();
    const integrations = AdminAccountIntegrationStore.getState().integrations;
    const userIntegrations = editState.formState.fieldValues.integrations;
    const userIntegrationsMap = this.getUserIntegrationsIdMap(userIntegrations, integrations);
    const jobOptions = JobStore.getState().jobs.map(j => ({label: j.name, value: j.id}));
    return {
      loggedInUser,
      hasSlack,
      hasSlackLoaded,
      // Main prop to inform/interact with the component <AdminEditUser />
      editState,
      // Account-wide dynamic data related to the user create/edit forms
      accountFormData,
      // Account-wide integrations used to identify integration_data/crm_data from their ids
      integrations,
      // Maps integration.id(s) to UserIntegrationExtern(s)
      userIntegrationsMap,
      // jobOptions for dropdown
      jobOptions,
    };
  }

  /**
   * Return a Map that maps an user's integration.id to itself
   * @param userIntegrations {List.Record}
   * @param integrations {List.Record}
   */
  static getUserIntegrationsIdMap(userIntegrations, integrations) {
    let map = Map();
    if (userIntegrations !== undefined && integrations !== undefined) {
      // If there are multiple `integrations` sharing the same `name+host`, then we combine them into one to support a
      // more streamlined, less confusing approach for the customer.
      for (const accountIntegration of integrations) {
        const repeated = map.filter(x => integrationsAreEqual(x, accountIntegration));
        const userIntegration = userIntegrations
          .filter(integ => integ.integrationId === accountIntegration.id)
          .first();

        let userIntegrationExtraId = accountIntegration.id;
        if (repeated && repeated.count() > 0) {
          for (const repeat of repeated) {
            map = map.remove(repeat[0]);
            userIntegrationExtraId = `${repeat[0]},${userIntegrationExtraId}`;
          }
        } else {
          userIntegrationExtraId = accountIntegration.id;
        }

        // Only render those who aren't in the array
        if (!IGNORED_INTEGRATIONS.includes(accountIntegration.name)) {
          map = map.set(
            userIntegrationExtraId,
            new UserIntegrationExtern({
              id: userIntegrationExtraId,
              name: displayNameForIntegration(accountIntegration),
              label: accountIntegration.label,
              host: accountIntegration.host,
              externId: userIntegration && userIntegration.externId,
              type: accountIntegration.type,
            })
          );
        }
      }
    }
    return map;
  }

  render() {
    return (
      <AltContainer stores={{AdminEditUserStore}}>
        <AdminEditUser {...this.props} />
      </AltContainer>
    );
  }
}
